import { useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { COMPUTE_ENGINE_KEEP_ALIVE_INTERVAL } from '@mpm-druid/constants';
import api, { QueryConfig } from '@shared/api';

interface GetComputeEngineResponse {
  computeURL: string;
}

const GET_PYTHON_PANEL_URL = '/api/get-python-panel-url';

const getComputeEngineURL = async (
  workspaceId: string,
  signal: AbortSignal | undefined
): Promise<string> => {
  const response = await api.get<GetComputeEngineResponse>(
    'code-panels/mpm/compute/get',
    { params: { workspaceId }, signal }
  );

  return response?.data?.computeURL.replace(GET_PYTHON_PANEL_URL, '');
};

async function keepAliveComputeEngine(computeEngineURL: string) {
  await api.get<void>(`${computeEngineURL}/api/keep-alive`);
}

async function restartComputeEngine() {
  await api.get<void>('code-panels/compute/kill');
}

const useComputeEngineURL = (
  { workspaceId }: { workspaceId: string },
  config?: QueryConfig<string>
) => {
  const queryClient = useQueryClient();

  const query = useQuery(
    ['mpm-python-panel-compute-engine-url', { workspaceId }],
    ({ signal }) => getComputeEngineURL(workspaceId, signal),
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      ...config,
      enabled: (config?.enabled ?? true) && !!workspaceId
    }
  );

  const computeEngineURL = query.data;
  useEffect(() => {
    if (!computeEngineURL) return;

    const cleanup = () => {
      const entry = keepAliveMap.get(computeEngineURL);
      if (!entry) return;
      entry.count -= 1;
      if (entry.count <= 0) {
        clearInterval(entry.intervalId);
        keepAliveMap.delete(computeEngineURL);
      }
    };

    const existing = keepAliveMap.get(computeEngineURL);
    if (existing) {
      existing.count += 1;
      return cleanup;
    }

    const intervalId = setInterval(async () => {
      try {
        await keepAliveComputeEngine(computeEngineURL);
      } catch {
        clearInterval(intervalId);
        keepAliveMap.delete(computeEngineURL);
        await restartComputeEngine();
        await queryClient.invalidateQueries([
          'mpm-python-panel-compute-engine-url'
        ]);
      }
    }, COMPUTE_ENGINE_KEEP_ALIVE_INTERVAL);

    keepAliveMap.set(computeEngineURL, {
      intervalId,
      count: 1
    });

    return cleanup;
  }, [computeEngineURL, queryClient, workspaceId]);

  return query;
};

export default useComputeEngineURL;

const keepAliveMap = new Map<
  string,
  { intervalId: NodeJS.Timeout; count: number }
>();
